import {yupResolver} from '@hookform/resolvers/yup';
import {Add, Remove} from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import {Button, Card, Grid, IconButton, TextField, Typography} from '@mui/material';
import React, {useEffect, useMemo, useState} from 'react';
import {FormProvider, useFieldArray, useForm} from 'react-hook-form';
import {useDispatch, useSelector} from 'react-redux';
import {i18n} from 'src/i18n';
import authSelectors from 'src/modules/auth/authSelectors';
import yupFormSchemas from 'src/modules/shared/yup/yupFormSchemas';
import actions from 'src/modules/user/form/userFormActions';
import userEnumerators from 'src/modules/user/userEnumerators';
import TenantAdminApi from 'src/modules/tenantAdmin/tenantAdminApi';
import {CUSTOM_ROLE_PREFIX, isCustomRoleToken} from 'src/security/customRoleConstants';
import DivisionAutocompleteFormItem from 'src/view/division/autocomplete/DivisionAutocompleteFormItem';
import {magicFlexBox} from 'src/theme/schemes/CustomTheme';
import InputFormItem from 'src/view/shared/form/items/InputFormItem';
import SelectFormItem from 'src/view/shared/form/items/SelectFormItem';
import FormWrapper, {FormButtons} from 'src/view/shared/styles/FormWrapper';
import * as yup from 'yup';

const schema = yup.object().shape({
  roles: yupFormSchemas.stringArray(i18n('user.fields.roles')),
  sucursales: yupFormSchemas.relationToMany(i18n('user.fields.sucursales'), {}),
  puesto: yupFormSchemas.relationToOne(i18n('user.fields.puesto'), {}),
  divisionIds: yupFormSchemas.relationToMany(i18n('user.fields.directDivisions'), { required: false }),
});

type RoleOption = {value: string; label: string};

function customRoleOptionsFromRoles(roles: unknown): RoleOption[] {
  if (!Array.isArray(roles)) return [];
  return [...new Set(roles.filter((r) => isCustomRoleToken(String(r))).map((r) => String(r)))].map(
    (value) => ({
      value,
      label: `${value}${i18n('user.customRoleSuffix')}`,
    }),
  );
}

function catalogToCustomRoleOptions(rows: any[]): RoleOption[] {
  return (rows || []).map((r: any) => ({
    value: `${CUSTOM_ROLE_PREFIX}${r.id}`,
    label: `${r.name}${i18n('user.customRoleSuffix')}`,
  }));
}

function mergeCustomRoleOptions(seed: RoleOption[], catalog: RoleOption[]): RoleOption[] {
  const catalogValues = new Set(catalog.map((o) => o.value));
  return [...catalog, ...seed.filter((o) => !catalogValues.has(o.value))];
}

function rolesPayloadForEdit(
  submitted: unknown,
  initial: unknown,
  selectableCustom: Set<string>,
): string[] {
  const formRoles = Array.isArray(submitted)
    ? submitted.map((r) => String(r || '').trim()).filter(Boolean)
    : [];
  const initialRoles = Array.isArray(initial)
    ? initial.map((r) => String(r || '').trim()).filter(Boolean)
    : [];
  const formSet = new Set(formRoles);
  const keptUnlisted = initialRoles.filter(
    (r) => isCustomRoleToken(r) && !selectableCustom.has(r) && !formSet.has(r),
  );
  return [...new Set([...formRoles, ...keptUnlisted])];
}

function UserEditForm(props) {
  const dispatch = useDispatch();

  const [initialValues] = useState(() => {
    const initUser = props.user || {};
    const raw = initUser.divisionIds;
    initUser.divisionIds = Array.isArray(raw)
      ? raw
          .map((d: any) => (d && typeof d === 'object' ? d.id ?? d._id : d))
          .map((id: any) => (id != null && id !== '' ? String(id).trim() : ''))
          .filter(Boolean)
      : [];
    if (!initUser.dashboardLinks?.length) {
      let defaultDashboardLink = {slug: 'dashboard', name: 'Dashboard', description: '', img: '', link: ''};
      if (initUser.dashboardLink) {
        defaultDashboardLink.link = initUser.dashboardLink;
      }
      initUser.dashboardLinks = [defaultDashboardLink];
    }
    return initUser;
  });

  const form = useForm({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: initialValues,
  });

  const {
    fields: dashboardLinks,
    remove: dashboardLinksRemove,
    append: dashboardLinksAppend,
    insert: dashboardLinksInsert,
    move: dashboardLinksMove,
  } = useFieldArray({
    control: form.control,
    name: 'dashboardLinks',
  });

  const onReset = () => {
    Object.keys(initialValues).forEach((key) => {
      form.setValue(key, initialValues[key]);
    });
  };

  const currentTenant = useSelector(authSelectors.selectCurrentTenant);
  const currentUser = useSelector(authSelectors.selectCurrentUser);
  const tenantAccessContextLoadStatus = useSelector(authSelectors.selectTenantAccessContextLoadStatus);

  const isLuxottica = true; //currentTenant?.id === '66900c307b9c0b7b3e460a64';

  var roles: any;
  roles = [];

  currentUser.tenants
    .filter((tenant) => tenant.tenant.id === currentTenant.id)
    .map((user) => {
      user.roles.map((role) => {
        roles.push(role);
      });
    });

  const [customRoleOptions, setCustomRoleOptions] = useState<RoleOption[]>(() =>
    customRoleOptionsFromRoles(initialValues.roles),
  );

  useEffect(() => {
    if (!currentTenant?.id) return;
    if (tenantAccessContextLoadStatus !== 'loaded') {
      return;
    }
    let cancelled = false;
    TenantAdminApi.customRolesList(currentTenant.id, {limit: 300})
      .then((res) => {
        if (cancelled) return;
        const catalog = catalogToCustomRoleOptions(res.data?.rows || []);
        setCustomRoleOptions((prev) => mergeCustomRoleOptions(prev, catalog));
      })
      .catch(() => {
        // Conservar tokens ya asignados si el catálogo no está disponible.
      });
    return () => {
      cancelled = true;
    };
  }, [currentTenant?.id, tenantAccessContextLoadStatus]);

  const roleOptions = useMemo(() => {
    const base = userEnumerators.roles
      .filter((value) => (value === 'admin' && roles.includes('admin')) || value !== 'admin')
      .map((value) => ({
        value,
        label: i18n(`roles.${value}.label`),
      }));
    return [...base, ...customRoleOptions];
  }, [roles, customRoleOptions]);

  const onSubmit = (values) => {
    const data = {
      id: props.user.id,
      ...initialValues,
      ...values,
    };
    delete data.email;
    if (Array.isArray(data.divisionIds)) {
      data.divisionIds = data.divisionIds.map((d: any) => d?.id ?? d).filter(Boolean);
    }
    data.roles = rolesPayloadForEdit(
      values.roles,
      initialValues.roles,
      new Set(customRoleOptions.map((o) => o.value)),
    );
    dispatch(actions.doUpdate(data, props.drawer));
    if (props.onSubmit) props.onSubmit(values);
  };

  return (
    <FormWrapper>
      <FormProvider {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          style={
            {
              ...magicFlexBox,
              paddingBottom: 18,
              paddingRight: 18,
              overflow: 'auto',
            } as any
          }>
          <Grid spacing={2} container>
            {props.drawer && (
              <Grid item xs={12}>
                <h1>{i18n('user.edit.title')}</h1>
              </Grid>
            )}
            <Grid item lg={7} md={8} sm={12} xs={12}>
              <TextField
                id="email"
                name="email"
                label={i18n('user.fields.email')}
                value={props.user.email}
                fullWidth
                margin={props.margin || 'normal'}
                InputProps={{
                  readOnly: true,
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                variant="outlined"
                size="small"
              />
            </Grid>

            <Grid item lg={7} md={8} sm={12} xs={12}>
              <SelectFormItem
                name="roles"
                label={i18n('user.fields.roles')}
                options={roleOptions}
                mode="multiple"
                selectAllButton={false}
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{mt: 0.5}}>
                {i18n('user.hints.customRolesInList')}
              </Typography>
            </Grid>

            <Grid item lg={7} md={8} sm={12} xs={12}>
              <DivisionAutocompleteFormItem
                name="divisionIds"
                label={i18n('user.fields.directDivisions')}
                required={false}
                mode="multiple"
                defaultValue={initialValues.divisionIds}
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{mt: 0.5}}>
                {i18n('user.hints.directDivisions')}
              </Typography>
            </Grid>

            {!isLuxottica && (
              <Grid item lg={7} md={8} sm={12} xs={12}>
                <InputFormItem name="dashboardLink" required={false} label={i18n('user.fields.dashboardLink')} />
              </Grid>
            )}
            {isLuxottica && (
              <Grid item xs={12}>
                <Typography variant="h6">Dashboard Links</Typography>
                <Grid container gap={2}>
                  {dashboardLinks.map((item, index) => (
                    <Grid key={item.id} item lg={7} md={8} sm={11} xs={11}>
                      <Card sx={{p: 2}}>
                        <InputFormItem name={`dashboardLinks[${index}].slug`} label="Slug" required />
                        <InputFormItem name={`dashboardLinks[${index}].name`} label="Nombre" required />
                        <InputFormItem name={`dashboardLinks[${index}].link`} label="Link" required />
                        <InputFormItem name={`dashboardLinks[${index}].description`} label="Descripción" />
                        <InputFormItem name={`dashboardLinks[${index}].img`} label="Image URL" />
                      </Card>
                      <Grid item xs={1} sx={{float: 'right'}}>
                        <IconButton color="secondary" onClick={() => dashboardLinksRemove(index)}>
                          <Remove />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
                <Button
                  type="button"
                  disabled={props.saveLoading}
                  onClick={() => dashboardLinksAppend({slug: '', name: '', description: '', img: '', link: ''})}
                  startIcon={<Add />}
                  size="small">
                  Agregar Link
                </Button>
              </Grid>
            )}

            {/* <Grid item lg={7} md={8} sm={12} xs={12}>
              <SucursalAutocompleteFormItem
                name="sucursales"
                label={i18n('user.fields.sucursales')}
                required={false}
                showCreate={!props.modal}
                mode="multiple"
              />
            </Grid> */}

            {/* <Grid item lg={7} md={8} sm={12} xs={12}>
              <PuestoAutocompleteFormItem
                name="puesto"
                label={i18n('user.fields.puesto')}
                required={false}
                showCreate={!props.modal}
              />
            </Grid> */}
          </Grid>
        </form>

        <FormButtons>
          <Button
            variant="contained"
            color="primary"
            disabled={props.saveLoading}
            type="button"
            onClick={form.handleSubmit(onSubmit)}
            startIcon={<SaveIcon />}
            size="small">
            {i18n('common.save')}
          </Button>

          <Button disabled={props.saveLoading} onClick={onReset} type="button" startIcon={<UndoIcon />} size="small">
            {i18n('common.reset')}
          </Button>

          {props.onCancel ? (
            <Button
              disabled={props.saveLoading}
              onClick={() => props.onCancel()}
              type="button"
              startIcon={<CloseIcon />}
              size="small">
              {i18n('common.cancel')}
            </Button>
          ) : null}
        </FormButtons>
      </FormProvider>
    </FormWrapper>
  );
}

export default UserEditForm;
